/**
 * One way to poll the server, for every poller in the app.
 *
 *   const p = createPoller({ key: 'sync-summary', run, base: 30000, max: 120000 });
 *   p.start();     // runs now, then on its own schedule
 *   p.poke();      // user did something: run now, back to the base interval
 *   p.reset();     // user did something we already have the answer to: base interval
 *   p.stop();
 *
 * `run(reason)` does one request and resolves to whether anything CHANGED
 * (truthy) or not (falsy). reason: 'start' | 'timer' | 'visible' | 'poke'.
 *
 *  - Hidden tab: nothing runs (document.hidden). When the tab comes back it
 *    runs once straight away, then carries on from the base interval.
 *  - Nothing changed: the wait doubles, base → 2×base → … → max.
 *    A change, a poke (user action) or the tab coming back resets it to base.
 *  - A failed run counts as "nothing changed" (so a dead server is asked
 *    less and less often, never hammered).
 *  - Never two requests for the same key at once, across every poller with
 *    that key: a run asked for while one is in flight joins that one.
 *
 * Only how OFTEN something is asked changes here; what is asked and what the
 * screen does with the answer stays with the caller.
 */

const inflight = new Map(); // key -> Promise<changed>
const queued = new Map();   // key -> the one run waiting behind it

function _hidden() {
  return typeof document !== 'undefined' && document.hidden === true;
}

export function createPoller({ key, run, base = 30000, max = base * 4, factor = 2, runOnStart = true }) {
  if (!key) throw new Error('createPoller needs a key');
  let timer = null;
  let running = false;
  let delay = base;
  let cadence = { base, max };
  let onVis = null;

  const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };

  const schedule = () => {
    clear();
    if (!running || _hidden()) return;
    timer = setTimeout(() => tick('timer'), delay);
  };

  const once = (reason) => {
    const existing = inflight.get(key);
    // A routine run already in flight answers a routine ask. The tab coming
    // back or a user action wants an answer from NOW: queue one more right
    // after it (still never two at once).
    if (existing) {
      if (reason !== 'visible' && reason !== 'poke') return existing;
      let next = queued.get(key);
      if (!next) {
        next = existing.then(() => { queued.delete(key); return once(reason); });
        queued.set(key, next);
      }
      return next;
    }
    let p;
    try {
      p = Promise.resolve(run(reason));
    } catch (e) {
      p = Promise.reject(e);
    }
    p = p.then((changed) => !!changed, () => false)
      .then((changed) => { inflight.delete(key); return changed; });
    inflight.set(key, p);
    return p;
  };

  function tick(reason) {
    clear();
    if (!running) return Promise.resolve(false);
    if (_hidden() && reason !== 'poke') return Promise.resolve(false);
    return once(reason).then((changed) => {
      // A change, a user action or the tab coming back: start again from the
      // base interval. Otherwise back off: base, 2×base, … up to max.
      const reset = changed || reason === 'poke' || reason === 'visible' || reason === 'start';
      delay = reset ? cadence.base : Math.min(Math.max(delay, cadence.base) * factor, cadence.max);
      schedule();
      return changed;
    });
  }

  const handleVisibility = () => {
    if (!running) return;
    if (_hidden()) { clear(); return; }
    tick('visible');
  };

  return {
    start() {
      if (running) return;
      running = true;
      delay = cadence.base;
      if (typeof document !== 'undefined') {
        onVis = handleVisibility;
        document.addEventListener('visibilitychange', onVis);
      }
      if (runOnStart) tick('start'); else schedule();
    },
    stop() {
      running = false;
      clear();
      if (onVis && typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
      onVis = null;
    },
    /** A user action: ask now, and go back to the base interval. */
    poke() { return running ? tick('poke') : Promise.resolve(false); },
    /** A user action that already has its answer: next ask at the base interval. */
    reset() { if (!running) return; delay = cadence.base; if (!inflight.has(key)) schedule(); },
    /** Change the interval range (e.g. a screen that needs it fresher is open). */
    setCadence(next) {
      const b = next && next.base > 0 ? next.base : cadence.base;
      const m = next && next.max > 0 ? Math.max(next.max, b) : Math.max(cadence.max, b);
      const faster = b < cadence.base || m < cadence.max;
      cadence = { base: b, max: m };
      // Faster than before: don't sit out the old, longer wait.
      if (faster && delay > b) { delay = b; schedule(); }
    },
    get running() { return running; },
    /** For tests and diagnostics. */
    get delay() { return delay; },
  };
}

// Tests only.
export const _resetPollers = () => { inflight.clear(); queued.clear(); };
